import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApiHeader, finalRouteApi } from '../../components/CommonApis/CommonApiUrl';
import { toast } from 'react-toastify';
import AdminBackNavigation from '../../components/CommonComponents/AdminBackNavigation';

// Build fresh auth header on each call to avoid stale tokens
const getAdminHeader = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: "Bearer " + (typeof window !== 'undefined' ? localStorage.getItem('access_token') : '')
});

const TYPES = ['text','textarea','select','multiselect','checkbox','radio','number','date','file'];

const PAGE_IDENTIFIERS = [
  { value: 'videos.create', label: 'Video - Create' },
  { value: 'videos.edit', label: 'Video - Edit' },
  { value: 'video_category.create', label: 'Video Category - Create' },
  { value: 'video_category.edit', label: 'Video Category - Edit' },
  { value: 'video_playlist.create', label: 'Video Playlist - Create' },
  { value: 'video_playlist.edit', label: 'Video Playlist - Edit' },

  { value: 'audio.create', label: 'Audio - Create' },
  { value: 'audio.edit', label: 'Audio - Edit' },
  { value: 'audio_category.create', label: 'Audio Category - Create' },
  { value: 'audio_category.edit', label: 'Audio Category - Edit' },
  { value: 'album.create', label: 'Album - Create' },
  { value: 'album.edit', label: 'Album - Edit' },
  { value: 'artist.create', label: 'Artist - Create' },
  { value: 'artist.edit', label: 'Artist - Edit' },

  { value: 'series.create', label: 'Series - Create' },
  { value: 'series.edit', label: 'Series - Edit' },
  { value: 'seasons.create', label: 'Season - Create' },
  { value: 'seasons.edit', label: 'Season - Edit' },
  { value: 'episodes.create', label: 'Episode - Create' },
  { value: 'episodes.edit', label: 'Episode - Edit' },
  { value: 'series_genre.create', label: 'Series Genre - Create' },
  { value: 'series_genre.edit', label: 'Series Genre - Edit' },

  { value: 'livestream.create', label: 'Live Stream - Create' },
  { value: 'livestream.edit', label: 'Live Stream - Edit' },
  { value: 'live_category.create', label: 'Live Category - Create' },
  { value: 'live_category.edit', label: 'Live Category - Edit' },

  { value: 'plans.create', label: 'Subscription Plan - Create' },
  { value: 'plans.edit', label: 'Subscription Plan - Edit' },
  { value: 'inapp_plan.create', label: 'In-app Plan - Create' },
  { value: 'inapp_plan.edit', label: 'In-app Plan - Edit' },
  { value: 'lifetime_subscription.settings', label: 'Lifetime Subscription - Settings' },

  { value: 'faq.create', label: 'FAQ - Create' },
  { value: 'faq.edit', label: 'FAQ - Edit' },
  { value: 'faq_category.create', label: 'FAQ Category - Create' },
  { value: 'faq_category.edit', label: 'FAQ Category - Edit' },

  { value: 'pages.create', label: 'Page - Create' },
  { value: 'pages.edit', label: 'Page - Edit' },

  { value: 'ads_category.create', label: 'Ads Category - Create' },
  { value: 'ads_category.edit', label: 'Ads Category - Edit' },
  { value: 'advertisement.create', label: 'Advertisement - Create' },
  { value: 'advertisement.edit', label: 'Advertisement - Edit' },

  { value: 'language.create', label: 'Language - Create' },
  { value: 'language.edit', label: 'Language - Edit' },
  { value: 'translate_language.create', label: 'Translate Language - Create' },
  { value: 'translate_language.edit', label: 'Translate Language - Edit' },
  { value: 'subtitle_language.create', label: 'Subtitle Language - Create' },
  { value: 'subtitle_language.edit', label: 'Subtitle Language - Edit' },

  { value: 'users.create', label: 'User - Create' },
  { value: 'users.edit', label: 'User - Edit' },

  { value: 'landing_page.create', label: 'Landing Page - Create' },
  { value: 'landing_page.edit', label: 'Landing Page - Edit' },

  { value: 'site_partner.create', label: 'Site Partner - Create' },
  { value: 'site_partner.edit', label: 'Site Partner - Edit' },

  { value: 'menu.create', label: 'Main Menu - Create' },
  { value: 'menu.edit', label: 'Main Menu - Edit' },
  { value: 'footer_menu.create', label: 'Footer Menu - Create' },
  { value: 'footer_menu.edit', label: 'Footer Menu - Edit' },
  { value: 'mobile_side_menu.create', label: 'Mobile Side Menu - Create' },
  { value: 'mobile_side_menu.edit', label: 'Mobile Side Menu - Edit' },

  { value: 'device.create', label: 'Device - Create' },
  { value: 'device.edit', label: 'Device - Edit' },
];

// Group pages by module for better alignment
const MODULE_PAGE_MAP = {
  Video: ['videos.create','videos.edit','video_category.create','video_category.edit','video_playlist.create','video_playlist.edit'],
  Audio: ['audio.create','audio.edit','audio_category.create','audio_category.edit','album.create','album.edit','artist.create','artist.edit'],
  Series: ['series.create','series.edit','series_genre.create','series_genre.edit'],
  Seasons: ['seasons.create','seasons.edit'],
  Episodes: ['episodes.create','episodes.edit'],
  LiveStream: ['livestream.create','livestream.edit','live_category.create','live_category.edit'],
  Plans: ['plans.create','plans.edit','inapp_plan.create','inapp_plan.edit','lifetime_subscription.settings'],
  FAQ: ['faq.create','faq.edit','faq_category.create','faq_category.edit'],
  Pages: ['pages.create','pages.edit'],
  Ads: ['ads_category.create','ads_category.edit','advertisement.create','advertisement.edit'],
  Language: ['language.create','language.edit','translate_language.create','translate_language.edit','subtitle_language.create','subtitle_language.edit'],
  Users: ['users.create','users.edit'],
  Menus: ['menu.create','menu.edit','footer_menu.create','footer_menu.edit','mobile_side_menu.create','mobile_side_menu.edit'],
  Landing: ['landing_page.create','landing_page.edit'],
  Partner: ['site_partner.create','site_partner.edit'],
  Devices: ['device.create','device.edit']
};

// Map legacy plural page identifiers to canonical singular forms
const ALIAS_TO_CANONICAL = {
  'audios.create': 'audio.create',
  'audios.edit': 'audio.edit',
  'livestreams.create': 'livestream.create',
  'livestreams.edit': 'livestream.edit',
  'faq_categories.create': 'faq_category.create',
  'faq_categories.edit': 'faq_category.edit',
  'advertisements.create': 'advertisement.create',
  'advertisements.edit': 'advertisement.edit',
  'languages.create': 'language.create',
  'languages.edit': 'language.edit',
  'landing_pages.create': 'landing_page.create',
  'landing_pages.edit': 'landing_page.edit',
  'partners.create': 'site_partner.create',
  'partners.edit': 'site_partner.edit',
};

const canonicalizePageId = (id) => ALIAS_TO_CANONICAL[id] || id;

export default function FieldFormEnhanced() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [form, setForm] = useState({ 
    name:'', 
    key:'', 
    type:'text', 
    required:false, 
    premium_only:false, 
    active:true, 
    validation:{}, 
    options:[] 
  });
  
  // Assignment state
  const [assignToPages, setAssignToPages] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({ 
    page_identifiers: [], 
    roles: '', 
    plans: '', 
    order: 0 
  });
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [visibleModules, setVisibleModules] = useState(Object.keys(MODULE_PAGE_MAP));

  useEffect(() => {
    let mounted = true;
    async function init() {
      // Reset state on field change to avoid leaking selections between fields
      setLoading(true);
      setAssignToPages(false);
      setExistingAssignments([]);
      setAssignmentForm({ page_identifiers: [], roles: '', plans: '', order: 0 });
      try {
        const feat = await axios.get(`${finalRouteApi}/custom-fields/dashboard`, { headers: getAdminHeader() });
        if (!mounted) return;
        if (!feat.data?.enabled) { setEnabled(false); } else { setEnabled(true); }
        
        // Fetch site settings to determine which modules are enabled/available
        try {
          const settingsRes = await axios.get(`${finalRouteApi}/setting/index`, { headers: getAdminHeader() });
          const st = settingsRes.data?.setting || {};
          const seriesEnabled = String(st.series_season || '0') === '1';
          const landingEnabled = String(st.enable_landing_page || '0') === '1';
          const liveEnabled = !!(st.rtmp_url || (st.enable_ppv_rent_live !== undefined));
          const enabledMods = {
            Video: true,
            Audio: true,
            Series: seriesEnabled,
            Seasons: seriesEnabled,
            Episodes: seriesEnabled,
            LiveStream: liveEnabled,
            Plans: true,
            FAQ: true,
            Pages: true,
            Ads: true,
            Language: true,
            Users: true,
            Landing: landingEnabled,
            Partner: true,
          };
          setVisibleModules(Object.entries(enabledMods).filter(([,v]) => v).map(([k])=>k));
        } catch (e) {
          // If settings fetch fails, leave all modules visible as fallback
          setVisibleModules(Object.keys(MODULE_PAGE_MAP));
        }
        
        if (editing) {
          try {
            // Debug: log which identifier we're fetching
            try { console.debug('[CustomFields] Fetching field', { id, url: `${finalRouteApi}/custom-fields/${id}` }); } catch(_){ }
            const res = await axios.get(`${finalRouteApi}/custom-fields/${id}`, { headers: getAdminHeader() });
            if (!mounted) return;
            if (res?.data?.field && typeof res.data.field === 'object') {
              setForm({ ...res.data.field, validation: res.data.field.validation || {}, options: res.data.field.options || [] });
            }
            // Fallback: if the single GET did not return an id, try listing all and find by id/key
            if (!res?.data?.field?.id) {
              try {
                const list = await axios.get(`${finalRouteApi}/custom-fields`, { headers: getAdminHeader() });
                const ident = String(id);
                const isNumericId = /^\d+$/.test(ident);
                const fieldsArr = list?.data?.fields || [];
                const found = fieldsArr.find(f => isNumericId ? String(f.id) === ident : (f?.key === ident || String(f.id) === ident));
                if (found) {
                  setForm({ ...found, validation: found.validation || {}, options: found.options || [] });
                }
              } catch (_) { /* ignore */ }
            }
          } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to fetch field';
            setError(msg);
            toast.error(`Failed to load field: ${msg}`);
          }
          // Fetch existing page assignments for this field (by numeric id or key)
          try {
            const isNumeric = /^\d+$/.test(String(id));
            const params = isNumeric ? { custom_field_id: id } : { custom_field_key: id };
            const asg = await axios.get(`${finalRouteApi}/custom-fields-assign`, { headers: getAdminHeader(), params });
            if (!mounted) return;
            const assignments = asg.data?.assignments || [];
            setExistingAssignments(assignments);
            setAssignmentForm(prev => ({
              ...prev,
              page_identifiers: assignments.map(a => canonicalizePageId(a.page_identifier))
            }));
            setAssignToPages(assignments.length > 0);
          } catch (e) {
            // On failure, keep assignments cleared and toggle off
            setExistingAssignments([]);
            setAssignmentForm(prev => ({ ...prev, page_identifiers: [] }));
            setAssignToPages(false);
          }
        }
      } catch (e) { 
        // Try the old endpoint if new one fails
        try {
          const feat = await axios.get(`${finalRouteApi}/admin/features/custom-fields`, { headers: getAdminHeader() });
          if (!mounted) return;
          setEnabled(feat.data?.enabled || false);
        } catch (err) {
          setError(err?.response?.data?.message || err.message); 
        }
        // Even if dashboard check fails, still attempt to load field and assignments when editing
        if (editing) {
          try {
            const res = await axios.get(`${finalRouteApi}/custom-fields/${id}`, { headers: getAdminHeader() });
            if (!mounted) return;
            if (res?.data?.field && typeof res.data.field === 'object') {
              setForm({ ...res.data.field, validation: res.data.field.validation || {}, options: res.data.field.options || [] });
            } else {
              // Fallback to list if single get did not shape as expected
              try {
                const list = await axios.get(`${finalRouteApi}/custom-fields`, { headers: getAdminHeader() });
                const ident = String(id);
                const isNumericId = /^\d+$/.test(ident);
                const fieldsArr = list?.data?.fields || [];
                const found = fieldsArr.find(f => isNumericId ? String(f.id) === ident : (f?.key === ident || String(f.id) === ident));
                if (found) {
                  setForm({ ...found, validation: found.validation || {}, options: found.options || [] });
                }
              } catch (_) { /* ignore */ }
            }
          } catch (err) {
            const msg = err?.response?.data?.message || err.message || 'Failed to fetch field';
            setError(prev => prev || msg);
          }
          try {
            const isNumeric = /^\d+$/.test(String(id));
            const params = isNumeric ? { custom_field_id: id } : { custom_field_key: id };
            const asg = await axios.get(`${finalRouteApi}/custom-fields-assign`, { headers: getAdminHeader(), params });
            if (!mounted) return;
            const assignments = asg.data?.assignments || [];
            setExistingAssignments(assignments);
            setAssignmentForm(prev => ({ ...prev, page_identifiers: assignments.map(a => canonicalizePageId(a.page_identifier)) }));
            setAssignToPages(assignments.length > 0);
          } catch (_) { 
            setExistingAssignments([]);
            setAssignmentForm(prev => ({ ...prev, page_identifiers: [] }));
            setAssignToPages(false);
          }
        }
      }
      finally { setLoading(false); }
    }
    init();
    return () => { mounted = false; };
  }, [id, editing]);

  const canSubmit = useMemo(() => 
    form.name.trim() && 
    form.key.trim() && 
    TYPES.includes(form.type) &&
    !saving
  , [form, saving]);

  const handlePageToggle = (pageValue) => {
    setAssignmentForm(prev => {
      const current = prev.page_identifiers;
      if (current.includes(pageValue)) {
        return { ...prev, page_identifiers: current.filter(p => p !== pageValue) };
      } else {
        return { ...prev, page_identifiers: [...current, pageValue] };
      }
    });
  };

  const handleSelectAllPages = () => {
    const allVisiblePages = visibleModules.flatMap(m => MODULE_PAGE_MAP[m] || []);
    setAssignmentForm(prev => ({
      ...prev,
      page_identifiers: allVisiblePages
    }));
  };

  const handleDeselectAllPages = () => {
    setAssignmentForm(prev => ({
      ...prev,
      page_identifiers: []
    }));
  };

  const submit = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      const payload = { ...form };
      if (!['select','multiselect','checkbox','radio'].includes(payload.type)) payload.options = [];

      // Enforce unique, non-empty field key across all custom fields
      const keyToCheck = (payload.key || '').trim();
      if (!keyToCheck) throw new Error('Field key is required');
      try {
        const listRes = await axios.get(`${finalRouteApi}/custom-fields`, { headers: getAdminHeader() });
        const fieldsArr = listRes?.data?.fields || [];
        const duplicate = fieldsArr.find(f => String(f.key) === keyToCheck && (!editing || String(f.id) !== String(id)));
        if (duplicate) {
          throw new Error(`Field key "${keyToCheck}" already exists (ID ${duplicate.id}). Please choose a unique key.`);
        }
      } catch (e) {
        if (e?.message?.startsWith('Field key')) { throw e; }
        // If list fetch fails, proceed (backend may still enforce uniqueness)
      }
      
      let fieldId = id;
      if (editing) {
        await axios.patch(`${finalRouteApi}/custom-fields/${id}`, payload, { headers: getAdminHeader() });
        setSuccess('Field updated successfully!');
        toast.success('Field updated successfully');
        // Update page assignments if changed
        try {
          const oldPages = new Set((existingAssignments || []).map(a => canonicalizePageId(a.page_identifier)));
          const newPages = new Set((assignmentForm.page_identifiers || []).map(p => canonicalizePageId(p)));

          const toDelete = (existingAssignments || []).filter(a => !newPages.has(canonicalizePageId(a.page_identifier)));
          const toAdd = [...newPages].filter(p => !oldPages.has(p));

          // Delete removed assignments
          for (const a of toDelete) {
            try { await axios.delete(`${finalRouteApi}/custom-fields-assign/${a.id}`, { headers: getAdminHeader() }); } catch (_) {}
          }
          // Create new assignments
          if (toAdd.length) {
            const assignPayloadBase = {
              custom_field_id: id,
              roles: assignmentForm.roles ? assignmentForm.roles.split(',').map(x => parseInt(x.trim())).filter(Boolean) : [],
              plans: assignmentForm.plans ? assignmentForm.plans.split(',').map(x => parseInt(x.trim())).filter(Boolean) : [],
              order: assignmentForm.order,
              active: true
            };
            for (const pageId of toAdd) {
              try { await axios.post(`${finalRouteApi}/custom-fields-assign`, { ...assignPayloadBase, page_identifier: pageId }, { headers: getAdminHeader() }); } catch (_) {}
            }
          }
          if (toDelete.length || toAdd.length) {
            toast.warn('Page assignments updated. Note: changing assignments may affect where existing values are shown on the frontend.');
          }
        } catch (_) {}
      } else {
        const createRes = await axios.post(`${finalRouteApi}/custom-fields`, payload, { headers: getAdminHeader() });
        fieldId = createRes.data?.field?.id || createRes.data?.id;
        
        // If assign to pages is enabled and we have a field ID, create assignments
        if (assignToPages && fieldId && assignmentForm.page_identifiers.length > 0) {
          const assignPayload = {
            custom_field_id: fieldId,
            roles: assignmentForm.roles ? assignmentForm.roles.split(',').map(x => parseInt(x.trim())).filter(Boolean) : [],
            plans: assignmentForm.plans ? assignmentForm.plans.split(',').map(x => parseInt(x.trim())).filter(Boolean) : [],
            order: assignmentForm.order,
            active: true
          };
          
          // Create assignment for each selected page
          const assignmentPromises = assignmentForm.page_identifiers.map(pageId => 
            axios.post(`${finalRouteApi}/custom-fields-assign`, {
              ...assignPayload,
              page_identifier: pageId
            }, { headers: getAdminHeader() })
          );
          
          await Promise.all(assignmentPromises);
          setSuccess(`Field created and assigned to ${assignmentForm.page_identifiers.length} page(s)!`);
          toast.success(`Field created and assigned to ${assignmentForm.page_identifiers.length} page(s)`);
        } else {
          setSuccess('Field created successfully!');
          toast.success('Field created successfully');
        }
      }
      
      setTimeout(() => navigate('/custom-fields'), 1500);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading…</div>;
  // If disabled, still allow edit view to render to surface potential fetch errors/info
  if (!enabled && !editing) return <div className="alert alert-warning m-4">Custom Fields addon is disabled. Please enable it from the plugin management page.</div>;

  return (
    <div className="p-4">
      <AdminBackNavigation path="/custom-fields" text="Back to Custom Fields" />
      <h2 className="mb-4">{editing ? 'Edit Field' : 'Create Custom Field'}</h2>
      
      {!enabled && editing && (
        <div className="alert alert-warning">Custom Fields addon is currently disabled. You can still view/edit the definition, but assignment APIs may be unavailable.</div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {editing && !loading && !error && !form?.id && (
        <div className="alert alert-info">No field data was loaded for identifier: {id}. Please check if the field exists and that you have access.</div>
      )}
      {success && <div className="alert alert-success">{success}</div>}
      
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">Field Configuration</h5>
          
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Field Name <span className="text-danger">*</span></label>
              <input 
                className="form-control" 
                placeholder="e.g., Company Name"
                value={form.name} 
                onChange={e => setForm(f => ({...f, name:e.target.value}))} 
              />
              {!form.name?.trim() && <small className="text-danger">Name is required</small>}
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Field Key <span className="text-danger">*</span></label>
              <input 
                className="form-control" 
                placeholder="e.g., company_name"
                value={form.key} 
                onChange={e => setForm(f => ({...f, key:e.target.value.replace(/\s+/g,'_').toLowerCase()}))} 
              />
              {!form.key?.trim() && <small className="text-danger">Key is required</small>}
              <small className="text-muted">This will be used as the unique identifier</small>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Field Type</label>
              <select 
                className="form-control" 
                value={form.type} 
                onChange={e => setForm(f => ({...f, type:e.target.value}))}
              >
                {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            
            <div className="col-md-8 mb-3">
              <label className="form-label">Field Settings</label>
              <div className="d-flex gap-4">
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="required"
                    checked={form.required} 
                    onChange={e => setForm(f => ({...f, required:e.target.checked}))} 
                  />
                  <label className="form-check-label" htmlFor="required">Required</label>
                </div>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="premium"
                    checked={form.premium_only} 
                    onChange={e => setForm(f => ({...f, premium_only:e.target.checked}))} 
                  />
                  <label className="form-check-label" htmlFor="premium">Premium Only</label>
                </div>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="active"
                    checked={form.active} 
                    onChange={e => setForm(f => ({...f, active:e.target.checked}))} 
                  />
                  <label className="form-check-label" htmlFor="active">Active</label>
                </div>
              </div>
            </div>
          </div>

          {['select','multiselect','checkbox','radio'].includes(form.type) && (
            <div className="mb-3">
              <label className="form-label">Options</label>
              {(form.options || []).map((o, idx) => (
                <div key={idx} className="input-group mb-2">
                  <input 
                    placeholder="Label" 
                    className="form-control" 
                    value={o.label} 
                    onChange={e => {
                      const arr = [...form.options]; 
                      arr[idx] = { ...arr[idx], label: e.target.value }; 
                      setForm(f => ({...f, options: arr}));
                    }} 
                  />
                  <input 
                    placeholder="Value" 
                    className="form-control" 
                    value={o.value} 
                    onChange={e => {
                      const arr = [...form.options]; 
                      arr[idx] = { ...arr[idx], value: e.target.value }; 
                      setForm(f => ({...f, options: arr}));
                    }} 
                  />
                  <button 
                    className="btn btn-outline-danger" 
                    onClick={() => setForm(f => ({...f, options: f.options.filter((_,i) => i!==idx)}))}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                className="btn btn-outline-secondary btn-sm" 
                onClick={() => setForm(f => ({...f, options:[...(f.options||[]), {label:'', value:''}]}))}
              >
                + Add Option
              </button>
            </div>
          )}
        </div>
      </div>

      {(
        <div className="card mt-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Page Assignment {editing ? '(Manage assigned pages)' : '(Optional)'}</h5>
            {editing && (
              <div className="alert alert-warning">
                Changing the pages where this field is assigned may cause existing values to not appear on those pages until reassigned appropriately.
              </div>
            )}
            
            <div className="form-check mb-3">
              <input 
                className="form-check-input" 
                type="checkbox"
                id="assignPages"
                checked={assignToPages} 
                onChange={e => setAssignToPages(e.target.checked)} 
              />
              <label className="form-check-label" htmlFor="assignPages">
                <strong>Assign this field to pages immediately after creation</strong>
              </label>
            </div>
            
            {assignToPages && (
              <>
                <div className="alert alert-info">
                  <strong>Quick Setup:</strong> Select the pages where this field should appear, and optionally restrict by user roles or subscription plans.
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Select Pages</label>
                  <div className="mb-2">
                    <button 
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={handleSelectAllPages}
                    >
                      Select All
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleDeselectAllPages}
                    >
                      Clear All
                    </button>
                  </div>
                  <div style={{maxHeight: '300px', overflowY: 'auto'}} className="border rounded p-2">
                    {Object.entries(MODULE_PAGE_MAP).filter(([moduleName]) => visibleModules.includes(moduleName)).map(([moduleName, pages]) => {
                      const allSelected = pages.every(p => assignmentForm.page_identifiers.includes(p));
                      return (
                        <div key={moduleName} className="mb-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <strong>{moduleName}</strong>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setAssignmentForm(prev => {
                                  const current = new Set(prev.page_identifiers);
                                  if (pages.every(p => current.has(p))) {
                                    // unselect all in group
                                    pages.forEach(p => current.delete(p));
                                  } else {
                                    // select all in group
                                    pages.forEach(p => current.add(p));
                                  }
                                  return { ...prev, page_identifiers: Array.from(current) };
                                });
                              }}
                            >
                              {allSelected ? 'Clear Group' : 'Select Group'}
                            </button>
                          </div>
                          <div className="row mt-2">
                            {pages.map(pageValue => {
                              const pageLabel = (PAGE_IDENTIFIERS.find(pi => pi.value === pageValue)?.label) || pageValue;
                              return (
                                <div key={pageValue} className="col-md-6 col-lg-4">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`page-${pageValue}`}
                                      checked={assignmentForm.page_identifiers.includes(pageValue)}
                                      onChange={() => handlePageToggle(pageValue)}
                                    />
                                    <label className="form-check-label" htmlFor={`page-${pageValue}`}>
                                      {pageLabel}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {assignmentForm.page_identifiers.length > 0 && (
                    <small className="text-success">
                      {assignmentForm.page_identifiers.length} page(s) selected
                    </small>
                  )}
                </div>
                
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">User Roles (Optional)</label>
                    <input 
                      className="form-control" 
                      placeholder="e.g., 1,2,3 (comma separated IDs)"
                      value={assignmentForm.roles} 
                      onChange={e => setAssignmentForm(f => ({...f, roles: e.target.value}))} 
                    />
                    <small className="text-muted">Leave empty to show for all roles</small>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Subscription Plans (Optional)</label>
                    <input 
                      className="form-control" 
                      placeholder="e.g., 1,2,3 (comma separated IDs)"
                      value={assignmentForm.plans} 
                      onChange={e => setAssignmentForm(f => ({...f, plans: e.target.value}))} 
                    />
                    <small className="text-muted">Leave empty to show for all plans</small>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Display Order</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={assignmentForm.order} 
                      onChange={e => setAssignmentForm(f => ({...f, order: parseInt(e.target.value || 0)}))} 
                    />
                    <small className="text-muted">Lower numbers appear first</small>
                  </div>
                </div>
                
                {assignmentForm.page_identifiers.length === 0 && (
                  <div className="alert alert-warning mb-0">
                    Please select at least one page to assign this field to.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        <button 
          className="btn btn-primary" 
          disabled={!canSubmit || saving} 
          onClick={submit}
        >
          {saving ? 'Saving...' : (
            editing ? 'Update Field' : 
            assignToPages && assignmentForm.page_identifiers.length > 0 ? 
            `Create & Assign to ${assignmentForm.page_identifiers.length} Page(s)` : 
            'Create Field'
          )}
        </button>
        <button 
          className="btn btn-secondary ms-2" 
          onClick={() => navigate('/custom-fields')}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
